import _ from 'lodash';

export class LRUCache {
    private readonly _maxSize: number;
    private _cache: Record<string, any>;

    constructor(maxSize: number) {
        this._maxSize = maxSize;
        this._cache = {};
    }

    public setCache(data: Record<string, any>) {
        this._cache = _.cloneDeep(data);
    }

    public setItem(key: string, value: any) {
        delete this._cache[key];

        this._cache[key] = value;

        this._removeLeastUsedItems();

        return this.setCache(this._cache);
    }

    public removeItem(key: string) {
        delete this._cache[key];
        return this.setCache(this._cache);
    }

    public removeAll() {
        console.debug('Removing all items in LRU cache');
        this._cache = {};
    }

    public getItem(key: string) {
        const item = this._cache[key];

        delete this._cache[key];
        this._cache[key] = item;

        return item;
    }

    private _removeLeastUsedItems() {
        const keys = Object.keys(this._cache);

        for (let i = keys.length - this._maxSize - 1; i >= 0; i--) {
            delete this._cache[keys[i]];
        }
    }
}

interface ILoadingCacheArgs {
    provider: (key: any) => Promise<any>;
    keyHasher?: (key: any) => string;
    maxSize: number;
    // TTL in seconds
    ttl?: number;
    shouldCacheFailures?: boolean;
    ttlForFailures?: number;
}

export class LoadingCache {
    private readonly _provider: (key: any) => Promise<any>;
    private readonly _keyHasher: (key: any) => string;
    private _cache: LRUCache;
    private readonly _ttl: number;
    private readonly _shouldCacheFailures: boolean;
    private readonly _ttlForFailures: number;

    constructor(args: ILoadingCacheArgs) {
        if (!_.isFunction(args.provider)) {
            throw new Error("Error: A function must be given for the 'provider' argument");
        } else {
            this._provider = args.provider;
        }
        this._keyHasher = args.keyHasher || JSON.stringify;
        if (!_.isFunction(this._keyHasher)) {
            throw new Error('Error: The keyHasher argument must be a function.');
        }
        const maxSize = args.maxSize || 100;
        this._cache = new LRUCache(maxSize);
        this._ttl = args.ttl;
        this._shouldCacheFailures = args.shouldCacheFailures || false;
        this._ttlForFailures = args.ttlForFailures || this._ttl;
    }

    public evict(key: any) {
        this._cache.removeItem(this._keyHasher(key));
    }

    public get(key: any) {
        const hashedKey = this._keyHasher(key);
        const hashedValue = this._cache.getItem(hashedKey);
        let promise: Promise<any>;
        if (hashedValue === undefined || (hashedValue.ttd && Date.now() / 1000 > hashedValue.ttd)) {
            console.debug('Entry for key: ' + hashedKey + ' has expired. Reloading.');
            promise = this._loadFromProvider(key);
        } else {
            promise = hashedValue.promise;
        }
        return promise;
    }

    public put(key: any, value: any) {
        const hashedKey = this._keyHasher(key);
        const promise = Promise.resolve(value);
        this._cache.setItem(hashedKey, { promise });
    }

    public clear() {
        this._cache.removeAll();
    }

    private _loadFromProvider(key: any) {
        const hashedKey = this._keyHasher(key);

        let promise: Promise<any>;
        try {
            promise = Promise.resolve(this._provider(key));
        } catch (err) {
            promise = Promise.reject(err);
        }

        this._cache.setItem(hashedKey, { promise });
        promise
            .then(() => {
                if (this._ttl) {
                    const currentItem = this._cache.getItem(hashedKey);
                    currentItem.ttd = Date.now() / 1000 + this._ttl;
                    this._cache.setItem(hashedKey, currentItem);
                }
            })
            .catch(() => {
                if (this._shouldCacheFailures && this._ttlForFailures) {
                    const currentItem = this._cache.getItem(hashedKey);
                    currentItem.ttd = Date.now() / 1000 + this._ttlForFailures;
                    this._cache.setItem(hashedKey, currentItem);
                } else {
                    this._cache.removeItem(hashedKey);
                }
            });

        return promise;
    }
}
